/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { AvatarContent } from '@atlaskit/avatar/avatar-content';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { GeneratedAvatar } from '@atlaskit/rovo-agent-components/ui/agent-avatar/GeneratedAvatar';

import type { SuggestionProfile } from '../../aiSuggestionsPluginType';
import { AgentVendorIcon, hasAgentVendorIcon } from './SuggestionCard/AgentVendorIcon';

const styles = cssMap({
	vendorIconInset: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		width: '100%',
	},
});

type SuggestionAvatarProps = {
	hasBorder: boolean;
	profile?: SuggestionProfile;
	size: 'UNSAFE_xsmall' | 'small';
};

/** Renders every suggestion avatar in the same Atlaskit hexagon frame. */
export function SuggestionAvatar({ profile, size, hasBorder }: SuggestionAvatarProps): JSX.Element {
	const { formatMessage } = useIntl();
	const label = profile?.name ?? formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel);
	const picture = profile?.picture?.trim() || undefined;

	let content: JSX.Element | null;
	if (profile && hasAgentVendorIcon(profile.name)) {
		content = (
			<span css={styles.vendorIconInset}>
				<AgentVendorIcon
					agentName={profile.name}
					size={size === 'small' ? 24 : 20}
					fallback={<span />}
				/>
			</span>
		);
	} else if (picture) {
		// Avatar renders the picture content directly
		content = null;
	} else if (profile) {
		content = <GeneratedAvatar agentIdentityAccountId={profile.accountId} size={size} />;
	} else {
		content = <RovoHexIcon label={label} size={size === 'small' ? 'small' : 'xsmall'} />;
	}

	return (
		<Avatar
			appearance="hexagon"
			borderColor={hasBorder ? undefined : 'transparent'}
			label={label}
			name={profile?.name}
			size={size}
			src={picture}
		>
			{content && <AvatarContent>{content}</AvatarContent>}
		</Avatar>
	);
}
